import { useCallback, useContext, useMemo } from 'react';
import { LocalThemeModeContext } from '@/contexts/ThemeModeContext';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { log } from '@/logger/logger';
import { useUserStore } from '@/state/user.state';
import { UserPreferenceKey } from '@/types/auth.types';
import {
  clearStoredThemeMode,
  DEFAULT_THEME_MODE,
  readStoredThemeMode,
  writeStoredThemeMode,
} from '@/utils/themeMode.utils';
import type { EdgeTypePreference, ThemePreference, User, UserPreferenceValue } from '@/types/auth.types';

const logger = log.getLogger('useUserPreferences');

export const useUserPreferences = () => {
  const setLocalThemeMode = useContext(LocalThemeModeContext);
  // Subscribe to the full user object but only recalculate when preferences change
  const user: User | null = useUserStore((state) => state.user);
  const setUser = useUserStore((state) => state.setUser);

  // Memoize reactive preference values - only recalculate when preferences object changes
  const userPrefOnScroll = useMemo(() => user?.preferences?.panOnScroll ?? true, [user?.preferences]);
  const userPrefShowFloatMenuOnRightClick = useMemo(
    () => user?.preferences?.showFloatMenuOnRightClick ?? true,
    [user?.preferences],
  );
  const userPrefRequireAltKeyForSuggestions = useMemo(
    () => user?.preferences?.requireAltKeyForSuggestions ?? false,
    [user?.preferences],
  );
  const userPrefEdgeType = useMemo(
    (): EdgeTypePreference => user?.preferences?.edgeType ?? 'bezier',
    [user?.preferences],
  );
  const userPrefSnapToGrid = useMemo(() => user?.preferences?.snapToGrid ?? false, [user?.preferences]);
  const userPrefSnapToNodes = useMemo(() => user?.preferences?.snapToNodes ?? false, [user?.preferences]);
  const userPrefTheme = useMemo(
    (): ThemePreference => user?.preferences?.theme ?? DEFAULT_THEME_MODE,
    [user?.preferences],
  );

  // Helper function to update user preferences in the store and API
  const updateUserPreference = useCallback(
    async (key: UserPreferenceKey, value: UserPreferenceValue) => {
      // Store original value for rollback
      const originalValue = user?.preferences?.[key];

      // Update Zustand store immediately (optimistic update)
      if (user) {
        setUser({
          ...user,
          preferences: {
            ...user.preferences,
            [key]: value,
          },
        });
      }

      try {
        await safeAxios.put(API_ROUTES.users.preferences(), {
          [key]: value,
        });
        return true;
      } catch (error) {
        logger.error('Error updating user preferences', error);
        // Rollback on error - restore original value
        if (user) {
          setUser({
            ...user,
            preferences: {
              ...user.preferences,
              [key]: originalValue,
            },
          });
        }
        return false;
      }
    },
    [user, setUser],
  );

  const handlePanOnScrollChange = useCallback(
    async (newValue: boolean) => {
      await updateUserPreference('panOnScroll', newValue);
    },
    [updateUserPreference],
  );

  const handleShowFloatMenuOnRightClickChange = useCallback(
    async (newValue: boolean) => {
      await updateUserPreference('showFloatMenuOnRightClick', newValue);
    },
    [updateUserPreference],
  );

  const handleRequireAltKeyForSuggestionsChange = useCallback(
    async (newValue: boolean) => {
      await updateUserPreference('requireAltKeyForSuggestions', newValue);
    },
    [updateUserPreference],
  );

  const handleEdgeTypeChange = useCallback(
    async (newValue: EdgeTypePreference) => {
      await updateUserPreference('edgeType', newValue);
    },
    [updateUserPreference],
  );

  const handleSnapToGridChange = useCallback(
    async (newValue: boolean) => {
      await updateUserPreference('snapToGrid', newValue);
    },
    [updateUserPreference],
  );

  const handleSnapToNodesChange = useCallback(
    async (newValue: boolean) => {
      await updateUserPreference('snapToNodes', newValue);
    },
    [updateUserPreference],
  );

  /**
   * Persists the color mode and switches the UI live.
   *
   * Switching is live — no reload. `ColorTheme` re-renders with the new mode, which flips the
   * `data-preferred-theme` attribute, and every FPL token re-resolves through CSS. Nothing
   * snapshots a colour into JS state, so there is nothing to rebuild.
   *
   * This originally reloaded, on the grounds that the WASM designer latches its theme at graph
   * construction (`FlowContext`) and a live switch would strip the canvas out of step. That is
   * still true of `figmaThemeEnabled`, but the canvas does not read the colour mode at all — no
   * WASM or canvas code consumes `useThemeMode`. The latch protects a different flag, so the
   * reload was guarding nothing.
   *
   * If the canvas starts honouring the mode, either plumb mode changes through `WasmAPI`/`FlowGraph`
   * or reinstate a reload here.
   *
   * Restore the storage mirror if the save fails. The preference store rolls back in that case,
   * and the next page load must use the same mode as the current session.
   */
  const handleThemeChange = useCallback(
    async (newValue: ThemePreference) => {
      const previousMode = user?.preferences?.theme ?? readStoredThemeMode();
      writeStoredThemeMode(newValue);
      if (!user) {
        setLocalThemeMode(newValue);
        return;
      }
      if (!(await updateUserPreference('theme', newValue))) {
        if (previousMode) {
          writeStoredThemeMode(previousMode);
        } else {
          clearStoredThemeMode();
        }
      }
    },
    [updateUserPreference, user, setLocalThemeMode],
  );

  return {
    userPrefOnScroll,
    userPrefShowFloatMenuOnRightClick,
    userPrefRequireAltKeyForSuggestions,
    userPrefEdgeType,
    userPrefSnapToGrid,
    userPrefSnapToNodes,
    userPrefTheme,
    handlePanOnScrollChange,
    handleShowFloatMenuOnRightClickChange,
    handleRequireAltKeyForSuggestionsChange,
    handleEdgeTypeChange,
    handleSnapToGridChange,
    handleSnapToNodesChange,
    handleThemeChange,
  };
};
